import React, { useContext } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import CallButton from 'flow/components/CallButton';
import CallTransferButton from 'flow/components/CallTransferButton';
import { convertNumberToE164 } from 'flow/utils/call-utils';
import { extensionToCallIsInCallWithYou } from 'flow/utils/call-utils-TW';
import {
    selectLoggedInUserIsInCall,
    getNumLocalCalls,
    getPendingTransferCallKey,
    isCoListening,
} from 'flow/selectors/call-selectors';
import { selectTransferInDoubleCallEnabled } from 'flow/selectors/feature-flag-selectors';
import { getCountryCode } from 'flow/utils/extension-utils';
import { getOwnContact, getOwnExtensionKey } from 'flow/selectors/selectors';
import { getExtension } from 'flow/selectors/ExtensionSelectors';
import AppContext from 'flow/contexts/app-context';
import Tooltip, { OriginalPlacements } from './ui-components/Tooltip';

export interface CallAdaptiveButtonProps {
    extensionKey?: string;
    phoneNumber?: string;
    extensionToCall?: any;
    transferContact?: any;
    size?: 'small' | 'medium' | 'large';
    activeBg?: boolean;
    rounded?: boolean;
    conferenceDTO?: any;
    inactiveBgColor?: string;
    tooltipPlacement?: OriginalPlacements;
    /** Extra classes on the rendered call / transfer button. */
    className?: string;
    /** The rendered button's id, for a <label htmlFor> that activates it. */
    id?: string;
}

const CallAdaptiveButton: React.FC<CallAdaptiveButtonProps> = ({
    extensionKey,
    phoneNumber,
    extensionToCall: initialExtensionToCall,
    transferContact: initialTransferContact,
    size,
    activeBg,
    rounded,
    conferenceDTO,
    inactiveBgColor,
    tooltipPlacement,
    className,
    id,
}) => {
    const appContext = useContext(AppContext);
    const loggedInUserExtensionKey = useAppSelector(getOwnExtensionKey);
    const loggedInUserIsInCall = useAppSelector(selectLoggedInUserIsInCall);

    const transferExtensionContact = useAppSelector((state) =>
        extensionKey
            ? state.extensions.getIn(['extensions', extensionKey, 'contact'])
            : undefined,
    );
    const extensionToCall = useAppSelector(
        (state) =>
            initialExtensionToCall ||
            (extensionKey ? getExtension(state, extensionKey) : undefined),
    );
    const loggedInCustomer = useAppSelector((state) =>
        loggedInUserExtensionKey
            ? state.customer.get(loggedInUserExtensionKey)
            : undefined,
    );
    const loggedInUserContact = useAppSelector(getOwnContact);
    const myCountryCode = getCountryCode(loggedInUserContact, loggedInCustomer);
    const transferContact = extensionKey
        ? transferExtensionContact
        : initialTransferContact;
    const transferInDoubleCallEnabled = useAppSelector(
        selectTransferInDoubleCallEnabled,
    );
    const hasMoreThanOneActiveCall = useAppSelector(getNumLocalCalls) > 1;
    const hasPendingAttendedTransfer = !!useAppSelector(
        getPendingTransferCallKey,
    );
    const hasCoListeningSession = useAppSelector(isCoListening);
    const showCallTransferButton = useAppSelector(
        (state) => !extensionToCallIsInCallWithYou(state, extensionToCall),
    );

    const renderCallButton = () => {
        const e164Number = convertNumberToE164(phoneNumber, myCountryCode);
        return (
            <CallButton
                size={size}
                phoneNumber={e164Number}
                extensionToCall={extensionToCall}
                activeBg={activeBg}
                rounded={rounded}
                inactiveBgColor={inactiveBgColor}
                tooltipPlacement={tooltipPlacement}
                conferenceDTO={conferenceDTO}
                className={className}
                id={id}
            />
        );
    };

    const renderTransferButton = () => {
        const { i18next, phoneEnabled } = appContext || {};
        const displayTransferButton =
            phoneEnabled && !hasCoListeningSession && showCallTransferButton;
        const e164Number = convertNumberToE164(phoneNumber, myCountryCode);
        return displayTransferButton ? (
            <Tooltip
                label={`${i18next?.t('Tooltip.Title.Call')} / ${i18next?.t('General.Call.Transfer')}`}
                placement={tooltipPlacement}
            >
                <CallTransferButton
                    size={size}
                    extensionKey={extensionKey}
                    transferContact={transferContact}
                    number={e164Number}
                    inactiveBgColor={inactiveBgColor}
                    className={className}
                    id={id}
                />
            </Tooltip>
        ) : null;
    };

    if (
        !phoneNumber ||
        (!transferInDoubleCallEnabled &&
            loggedInUserIsInCall &&
            hasMoreThanOneActiveCall) ||
        hasPendingAttendedTransfer
    ) {
        return null;
    }

    return loggedInUserIsInCall ? renderTransferButton() : renderCallButton();
};

export default CallAdaptiveButton;
